import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { PostHog } from 'posthog-js'

interface PostHogContextValue {
  posthog: PostHog | null
}

const PostHogContext = createContext<PostHogContextValue>({ posthog: null })

// Exported hook for consuming PostHog context
// eslint-disable-next-line react-refresh/only-export-components
export const usePostHog = () => {
  const context = useContext(PostHogContext)
  if (!context) {
    throw new Error('usePostHog must be used within a PostHogProvider')
  }
  return context.posthog
}

interface PostHogProviderProps {
  children: ReactNode
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [client, setClient] = useState<PostHog | null>(null)

  useEffect(() => {
    let active = true
    let instance: PostHog | undefined

    // Load posthog-js lazily so the ~200KB library stays out of the initial
    // bundle (keeps the hero paint / LCP fast). Analytics is opt-out by default
    // until consent, and consumers re-apply consent once the client is ready.
    void import('posthog-js').then(({ default: posthog }) => {
      if (!active) return

      posthog.init('phc_ytsJDNKw4k8A0zpEq97MMXs64uzJ2CvgGiKStmHGoKj', {
        api_host: 'https://us.i.posthog.com',

        // Start opt-out by default - require consent
        opt_out_capturing_by_default: true,

        // Privacy settings
        respect_dnt: true,
        persistence: 'localStorage+cookie',
        person_profiles: 'identified_only',

        // Autocapture features
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,

        // Session recording with privacy
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-private]',
          recordCrossOriginIframes: false,
        },

        // Performance tracking
        capture_performance: true,
        enable_heatmaps: true,

        // Never capture sensitive data
        property_denylist: ['password', 'creditCard', 'ssn', 'email', 'phone'],

        // Debug in development
        loaded: (ph) => {
          if (import.meta.env.DEV) {
            ph.debug()
          }
        },
      })

      // Set app metadata
      posthog.register({
        app_version: '1.0.0',
        deployment_env: import.meta.env.MODE,
      })

      instance = posthog
      setClient(posthog)
    })

    return () => {
      active = false
      instance?.reset()
    }
  }, [])

  return (
    <PostHogContext.Provider value={{ posthog: client }}>
      {children}
    </PostHogContext.Provider>
  )
}
